/* eslint-disable @typescript-eslint/no-explicit-any */
import { FunctionalComponent, RefObject, createContext, h } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';
import { ROOT_ID } from '../../constants';
import { VIEW } from '../../enums';
import { kebabCase } from '../../helpers/kebabCase';
import { useUiContext } from '../UiContext';

export interface GlobalStateContextType {
  blockFocus: boolean;
  langSelectorOpen: boolean;
  nextFocus: RefObject<any> | null | string;
  setBlockFocus: (state: boolean) => void;
  setLangSelectorStatus: (state: boolean) => void;
  setNextFocus: (ref: RefObject<any> | string) => void;
  consumeNextFocus: () => void;
}

const initialValue = {
  blockFocus: false,
  consumeNextFocus: () => null,
  langSelectorOpen: false,
  nextFocus: null,
  setBlockFocus: () => null,
  setLangSelectorStatus: () => null,
  setNextFocus: () => null,
};

const GlobalStateContext = createContext<GlobalStateContextType>(initialValue);

export const GlobalStateContextProvider: FunctionalComponent = ({ children }) => {
  const { view } = useUiContext();
  const [block, setBlock] = useState(initialValue.blockFocus);
  const [nextFocus, setFocus] = useState<RefObject<any> | null | string>(initialValue.nextFocus);

  const [langSelectorOpen, setLangSelectorOpen] = useState<boolean>(initialValue.langSelectorOpen);
  const [prevFocus, setPrevFocus] = useState<RefObject<any> | null | string>(initialValue.nextFocus);

  const setGlobalFocusBlock = (state: boolean) => {
    setBlock(state);
  };

  const setLangSelectorStatus = (state: boolean) => {
    setLangSelectorOpen(state);
  };

  useEffect(() => {
    if (nextFocus === null) {
      setGlobalFocusBlock(false);
    }
  }, [nextFocus]);

  const setNextFocus = (ref: RefObject<any> | string) => {
    if (nextFocus !== null && view === VIEW.SECOND_LAYER) {
      setPrevFocus(nextFocus);
    } else {
      setPrevFocus(null);
    }
    setFocus(ref);
  };

  const consumeNextFocus = () => {
    if (typeof nextFocus === 'string') {
      try {
        const root = document.getElementById(ROOT_ID);
        const container =
          root?.shadowRoot?.querySelector<HTMLButtonElement>(`[data-testid=${kebabCase(nextFocus)}]`) || null;

        if (container !== null) {
          container.focus();
        }
      } catch (error) {
        //
      }
    } else {
      nextFocus?.current?.focus();

      if (prevFocus !== null) {
        setFocus(prevFocus);
      } else {
        setFocus(null);
      }
    }
  };

  return (
    <GlobalStateContext.Provider
      value={{
        blockFocus: block,
        consumeNextFocus,
        langSelectorOpen,
        nextFocus,
        setBlockFocus: setGlobalFocusBlock,
        setLangSelectorStatus,
        setNextFocus,
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  );
};

export const useGlobalStateContext = (): GlobalStateContextType => {
  return useContext(GlobalStateContext);
};
